import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { AuthStorageService } from '../services/auth/auth-storage.service';
import { TokenService } from '../services/auth/token.service';
import { SessionService } from '../services/smartclock/session.service';
import { SUPPORT_ENDED_URL } from '../services/support/support-paths';

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const tokenService = inject(TokenService);
  const sessionService = inject(SessionService);
  const authStorage = inject(AuthStorageService);
  const router = inject(Router);

  try {
    // Verificar si el usuario está autenticado y tiene tokens válidos
    const isAuthenticated = authService.isAuthenticated();
    const hasValidAccessToken = tokenService.hasValidAccessToken();
    const hasValidRefreshToken = tokenService.hasValidRefreshToken();

    // Si está autenticado y tiene access token válido, permitir acceso
    if (isAuthenticated && hasValidAccessToken) {
      return true;
    }

    // Si tiene refresh token válido pero no access token, el interceptor se encargará
    if (isAuthenticated && hasValidRefreshToken) {
      return true;
    }

    // «Ver como»: una sesión de soporte vencida o terminada no se "desloguea"
    // (no hay refresh ni /auth/logout): /support/ended sale del modo soporte
    // sin tocar la sesión normal de las otras pestañas.
    if (authStorage.isSupportMode()) {
      const expired = !!tokenService.getAccessToken() && !hasValidAccessToken;
      return router.createUrlTree([SUPPORT_ENDED_URL], {
        queryParams: { reason: expired ? 'expired' : 'ended' },
      });
    }

    // Si quedaba algún token (aunque ya vencido/inválido) O si el perfil en
    // memoria sigue cargado (ej. alguien borró el token directo de
    // localStorage, sin pasar por logout), hubo una sesión que expiró:
    // limpiarla y avisar en el login (REQ-A.2), en vez del genérico returnUrl
    // reservado para quien nunca inició sesión.
    const hadSession =
      !!tokenService.getAccessToken() ||
      !!tokenService.getRefreshToken() ||
      sessionService.current !== null;
    if (hadSession) {
      authService.logout();
      // createUrlTree (no router.navigate + return false): un navigate()
      // imperativo disparado DESDE un guard compite con la navegación que lo
      // está evaluando — con runGuardsAndResolvers:'always' esa carrera se
      // pierde seguido y el ?reason=session_expired nunca llega al login (el
      // aviso no se muestra). Devolver el UrlTree hace que el propio router
      // resuelva el redirect como parte de esta misma navegación, sin carrera.
      return router.createUrlTree(['/login'], { queryParams: { reason: 'session_expired' } });
    }

    return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
  } catch {
    // En caso de cualquier error, siempre redirigir al login
    return router.createUrlTree(['/login']);
  }
};
